import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { formatINR } from '@wonderherbals/shared';
import { useCartStore } from '../stores/useCartStore';
import { useUIStore } from '../stores/useUIStore';
import { WEB_ROUTES } from '@wonderherbals/shared';

export default function CartDrawer() {
  const { isCartOpen, closeCart } = useUIStore();
  const items = useCartStore((s) => s.items);
  const subtotal = useCartStore((s) => s.subtotal());
  const shipping = useCartStore((s) => s.shipping());
  const total = useCartStore((s) => s.total());
  const itemCount = useCartStore((s) => s.itemCount());
  const discount = useCartStore((s) => s.discount());
  const couponCode = useCartStore((s) => s.couponCode);
  const incrementQuantity = useCartStore((s) => s.incrementQuantity);
  const decrementQuantity = useCartStore((s) => s.decrementQuantity);
  const removeItem = useCartStore((s) => s.removeItem);
  const FREE_SHIPPING_THRESHOLD = useCartStore((s) => s.shippingRules.freeShippingThreshold);

  const amountToFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const shippingProgress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);
  const hasFreeShipping = subtotal >= FREE_SHIPPING_THRESHOLD;

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50"
            style={{ background: 'rgba(15,38,22,0.5)', backdropFilter: 'blur(4px)' }}
            onClick={closeCart}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 280, damping: 25 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={(_e, { offset, velocity }) => {
              if (offset.x > 100 || velocity.x > 500) {
                closeCart();
              }
            }}
            className="fixed inset-y-0 right-0 z-50 flex flex-col bg-white shadow-2xl"
            style={{ width: '100%', maxWidth: '420px' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid #DCEEC4' }}>
              <h2 className="font-serif text-xl" style={{ color: '#0F2616' }}>
                Your Cart <span style={{ color: '#4D6250', fontSize: '14px', fontFamily: "'Jost', sans-serif" }}>({itemCount})</span>
              </h2>
              <button
                onClick={closeCart}
                className="p-2 hover:bg-gray-50 transition-colors"
                style={{ borderRadius: '2px' }}
                aria-label="Close cart"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                </svg>
              </button>
            </div>

            {/* Free Shipping Progress Bar */}
            <div className="px-6 py-4" style={{ background: '#F4F8EC', borderBottom: '1px solid #DCEEC4' }}>
              {hasFreeShipping ? (
                <p className="text-sm font-medium" style={{ color: '#3F7A1F', fontFamily: "'Jost', sans-serif" }}>
                  🎉 You've unlocked Free Shipping!
                </p>
              ) : (
                <>
                  <p className="text-xs mb-2" style={{ color: '#4D6250', fontFamily: "'Jost', sans-serif" }}>
                    Add <strong style={{ color: '#0F2616' }}>{formatINR(amountToFreeShipping)}</strong> more for free shipping
                  </p>
                  <div className="w-full h-1.5" style={{ background: '#DCEEC4', borderRadius: '100px' }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${shippingProgress}%` }}
                      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                      style={{ height: '100%', background: '#3F7A1F', borderRadius: '100px' }}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {items.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-5xl mb-4">🌿</div>
                  <p className="font-serif text-xl mb-2" style={{ color: '#0F2616' }}>Your cart is empty</p>
                  <p className="text-sm mb-6" style={{ color: '#4D6250', fontFamily: "'Jost', sans-serif" }}>
                    Discover our Ayurvedic formulations
                  </p>
                  <button
                    onClick={closeCart}
                    className="px-8 py-3 text-sm font-medium tracking-wide text-white transition-all"
                    style={{ background: '#0F2616', borderRadius: '2px', fontFamily: "'Jost', sans-serif" }}
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                items.map((item) => (
                  <div key={`${item.productId}-${item.variant?.id || ''}`} className="flex gap-4">
                    <Link to={`/product/${item.slug}`} onClick={closeCart}>
                      <img
                        src={item.image || 'https://placehold.co/72x72/f0ebe3/1a3a2a?text=WH'}
                        alt={item.name}
                        className="w-[72px] h-[72px] object-cover flex-none"
                        style={{ borderRadius: '4px' }}
                        loading="lazy"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/product/${item.slug}`} onClick={closeCart}>
                        <h3 className="text-sm font-medium leading-snug line-clamp-2 hover:underline" style={{ color: '#0F2616', fontFamily: "'Jost', sans-serif" }}>
                          {item.name}
                        </h3>
                      </Link>
                      {item.variant && (
                        <p className="text-xs mt-0.5" style={{ color: '#4D6250', fontFamily: "'Jost', sans-serif" }}>{item.variant.name}</p>
                      )}
                      <p className="text-sm font-medium mt-1" style={{ color: '#0F2616', fontFamily: "'Jost', sans-serif" }}>
                        {formatINR(item.price)}
                      </p>

                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex items-center border" style={{ borderColor: '#DCEEC4', borderRadius: '2px' }}>
                          <button
                            onClick={() => decrementQuantity(item.productId, item.variant?.id)}
                            className="w-8 h-8 flex items-center justify-center text-sm hover:bg-gray-50 transition-colors"
                            style={{ color: '#0F2616' }}
                          >−</button>
                          <span className="w-8 text-center text-sm" style={{ fontFamily: "'Jost', sans-serif" }}>{item.quantity}</span>
                          <button
                            onClick={() => incrementQuantity(item.productId, item.variant?.id)}
                            className="w-8 h-8 flex items-center justify-center text-sm hover:bg-gray-50 transition-colors"
                            style={{ color: '#0F2616' }}
                          >+</button>
                        </div>
                        <button
                          onClick={() => removeItem(item.productId, item.variant?.id)}
                          className="text-xs hover:underline transition-colors ml-auto"
                          style={{ color: '#C0392B', fontFamily: "'Jost', sans-serif" }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="px-6 py-5 space-y-3" style={{ borderTop: '1px solid #DCEEC4' }}>
                {/* Summary */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm" style={{ fontFamily: "'Jost', sans-serif", color: '#4D6250' }}>
                    <span>Subtotal</span>
                    <span>{formatINR(subtotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-sm" style={{ fontFamily: "'Jost', sans-serif", color: '#3F7A1F' }}>
                      <span>Discount {couponCode && `(${couponCode})`}</span>
                      <span>−{formatINR(discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm" style={{ fontFamily: "'Jost', sans-serif", color: '#4D6250' }}>
                    <span>Shipping</span>
                    <span style={{ color: hasFreeShipping ? '#3F7A1F' : '#0F2616' }}>
                      {hasFreeShipping ? 'Free ✓' : formatINR(shipping)}
                    </span>
                  </div>
                  <div className="flex justify-between font-medium pt-2" style={{ borderTop: '1px solid #DCEEC4', fontFamily: "'Jost', sans-serif" }}>
                    <span style={{ color: '#0F2616' }}>Total</span>
                    <span style={{ color: '#0F2616' }}>{formatINR(total)}</span>
                  </div>
                  <p className="text-xs" style={{ color: '#4D6250', fontFamily: "'Jost', sans-serif" }}>GST included · All prices in INR</p>
                </div>

                {/* Checkout CTA */}
                <Link
                  to={WEB_ROUTES.CHECKOUT}
                  onClick={closeCart}
                  className="flex items-center justify-center w-full py-4 text-sm font-medium tracking-wide text-white transition-all hover:opacity-90"
                  style={{ background: '#0F2616', borderRadius: '2px', fontFamily: "'Jost', sans-serif", letterSpacing: '0.08em' }}
                >
                  Proceed to Checkout →
                </Link>

                {/* Trust strip */}
                <div className="flex items-center justify-center gap-4 pt-1">
                  {['🔒 Secure', 'Razorpay', '100% Authentic'].map((t) => (
                    <span key={t} className="text-[10px]" style={{ color: '#4D6250', fontFamily: "'Jost', sans-serif" }}>{t}</span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
